import { IUserContextProvider, RoleType } from './iam.types';
import { UserProfile } from './user-profile';
import { action, makeAutoObservable, observable } from 'mobx';
import { IDispatcher } from '../../web/elements-types/src';
import { roleMapper, roleImpersonatedMapper } from './authorization.utils';
import { AzureAdToken } from './azure-ad.types';
import { get } from '../../../hooks/http/use-get-element';
import { ActiveRoute } from '../../../components-common/routes';

function getHighestRole(roles: string[]): RoleType {
  if (roles.includes('Access.Admin')) return 'STUDY_DIRECTOR';
  if (roles.includes('Access.Lecturer')) return 'LECTURER';
  if (roles.includes('Access.Student')) return 'STUDENT';

  throw new Error('No supported role found');
}

export class SessionStore implements IUserContextProvider {
  @observable userProfile: undefined | UserProfile = undefined;
  currentView: string | undefined = undefined;
  constructor(private dispatcher: IDispatcher) {
    makeAutoObservable(this);
  }
  userId = this.userProfile?.id!;
  role = this.userProfile?.role!;
  roleImpersonated = this.userProfile?.roleImpersonated!;

  get context() {
    return this.userProfile!;
  }

  @action
  setUserProfile(userProfile: UserProfile) {
    const { role, roleImpersonated, ...rest } = userProfile;
    const userRole = roleMapper[role] || roleMapper[`Access.${role}`];
    this.userProfile = {
      role: userRole,
      roleImpersonated: this.calculateRoleImpersonated(userRole),
      ...rest,
    };
    this.dispatcher.notify({
      type: 'user-profile-changed',
      payload: this.userProfile,
    });
  }

  @action
  setImpersonatedRole(role: any) {
    if (this.userProfile) {
      this.userProfile.roleImpersonated = role;
    }
  }

  @action
  setProfileFromToken(token: AzureAdToken) {
    const {
      idTokenClaims: { oid, roles },
      name,
      username,
      localAccountId,
    } = token;

    if (!roles)
      throw new Error(
        'The accessToken generated by AzureB2C must contain an attribute "roles" with an array that must contain the roles for the current logged in user.'
      );

    if (roles.length === 0)
      throw new Error(
        'The roles property in the accessToken generated by AzureB2C must contain at least one role for the current logged in user.'
      );

    const role = getHighestRole(roles);
    const roleImpersonated = role;
    const profile = {
      id: oid,
      name,
      username,
      role,
      roleImpersonated,
      email: username,
      localAccountId,
    };
    this.setUserProfile(profile as any);
  }

  calculateRoleImpersonated(role: RoleType) {
    const location = window.location.pathname;
    if (role === 'STUDENT' || location.startsWith('/students')) {
      return 'STUDENT';
    }
    if (location.startsWith('/lecturers')) {
      return 'LECTURER';
    }
    if (role === 'STUDY_DIRECTOR' && location.startsWith('/management')) {
      return 'STUDY_DIRECTOR';
    }
    return 'LECTURER';
  }
}
